import { SIZES, SPACING } from '@alto/design-library-tokens';
import { ActionSheetProvider, LoadingEmptyState, ToastProvider, useScreenSize } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
// Deep imports (not the '@alto/features' barrel) so the eager shell doesn't pull the entire
// features package into the initial `main` bundle — it now loads via the lazy app-routes chunk.
// The barrel-shortform convention is intentionally bypassed here for that reason.
// eslint-disable-next-line @alto/prefer-alto-features-shortform -- deep import keeps the @alto/features barrel out of the eager main bundle
import { AppVersionWatcher } from '@alto/features/src/app-version/components/AppVersionWatcher';
// eslint-disable-next-line @alto/prefer-alto-features-shortform -- deep import keeps the @alto/features barrel out of the eager main bundle
import { ExperimentationListener } from '@alto/features/src/experimentation/components/ExperimentationListener';
import { Auth0Provider } from '@auth0/auth0-react';
import React, { Suspense, lazy, useCallback, useEffect } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
// eslint-disable-next-line import/no-deprecated
import { fetchAddresses } from '~shared/actions/addresses';
import { setPostLoginRoute } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchPrescriptions, fetchTransfers } from '~shared/actions/prescriptions';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { getAuth0ClientIDForEnv, getAuth0DomainForEnv } from '~shared/config';
import { useFetchNextAvailableDatesForCart } from '~shared/features/next-available-date/hooks/useFetchNextAvailableDatesForCart';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { queries } from '~shared/queries/query-keys';
import { invalidateDeliveries } from '~shared/queries/query-keys/deliveries';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useDeliveriesReduxBridge } from '~shared/queries/useDeliveries';
import { usePrescriptionPricingReduxBridge } from '~shared/queries/usePrescriptionPricing';
import { useShipmentPaymentMethodsReduxBridge } from '~shared/queries/useShipmentPaymentMethods';
import { useShipmentsReduxBridge } from '~shared/queries/useShipments';
import { QueryClientProvider } from '~shared/react-query';
import { ReactQueryDevtools } from '~shared/react-query/devtools';
import { queryClient } from '~shared/react-query/queryClient';
import { Sentry } from '~shared/sentry';
import { ReduxProvider, useDispatchShared, useSelectorShared } from '~shared/store';
import { configureStore } from '~web/configureStore';
import { NavigationProvider, ThemeProvider } from '~web/contexts';
import { Welcome } from '~web/features/auth/components/Welcome';
import { useBrowserTab, useGetInitialData } from '~web/hooks';
// Login renders eagerly (in `main`), outside the lazy route chunk, so the post-auth0
// login flow mounts synchronously — same timing as before the code-split — and doesn't
// bounce login → home → login. WelcomePage is deep-imported so it doesn't pull the barrel.
// Lazy-loaded so the entire route tree (all screens + the @alto/features barrel +
// React Compiler output) ships as deferred chunks instead of in the initial `main`.
const WebRoutes = lazy(() =>
  import(/* webpackChunkName: "app-routes" */ '~web/routes').then((m) => ({ default: m.WebRoutes })),
);

// avoid 'auth0-spa-js must run on a secure origin' error
// when running app locally pointed at server running locally
// we can remove this code when url for that scenario is localhost or https
if (process.env.BACKEND_ENVIRONMENT === 'local' && !window.crypto?.subtle) {
  Object.defineProperty(window, 'crypto', {
    writable: true,
    value: {
      subtle: { digest: () => 'foo' },
      getRandomValues: () => new Uint8Array(16),
    },
  });
}

type Props = {
  readonly store: any;
  readonly skipInitialDataFetch?: boolean;
};

const DataLoader = () => {
  const authorized = useSelectorShared((state) => state.auth.authorized);
  const dispatch = useDispatchShared();
  const fetchNextAvailableDatesForCart = useFetchNextAvailableDatesForCart();

  useGetInsurances();
  useGetPaymentMethods();
  // Bridge: keep the Redux shipmentPaymentMethods slice in sync from React Query
  // so checkout's getOrder selector keeps working (transitional bridge toward strategy A;
  // see the redux-thunk-to-react-query-migration skill).
  useShipmentPaymentMethodsReduxBridge();
  // Bridge: keep the Redux shipments slice in sync from React Query (transitional bridge
  // toward strategy A; see the redux-thunk-to-react-query-migration skill).
  useShipmentsReduxBridge();
  // Bridge: keep the Redux deliveries slice in sync from React Query (transitional bridge
  // toward strategy A; see the redux-thunk-to-react-query-migration skill).
  useDeliveriesReduxBridge();
  // Bridge: keep the Redux pricing.prescriptionPricing slice in sync from React Query
  // so the pricing selectors keep working (transitional bridge toward strategy A;
  // see the redux-thunk-to-react-query-migration skill).
  usePrescriptionPricingReduxBridge();

  useEffect(() => {
    if (!authorized) return;
    fetchNextAvailableDatesForCart();
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchTransfers());
    queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchPrescriptions(true));
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchUsers());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchAddresses());
    void invalidateDeliveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, dispatch]);

  return null;
};

const PatientDashboardApp = ({ store, skipInitialDataFetch = false }: Props) => {
  useBrowserTab();

  const { persistor } = configureStore();
  const { getInitialData } = useGetInitialData({ store });

  useEffect(() => {
    if (skipInitialDataFetch) return;
    getInitialData();
  }, [getInitialData, skipInitialDataFetch]);

  const createAnalyticsEvent = useCallback(
    (actionSheetName: string) => {
      store.dispatch(sendAnalyticsEvent(createEvent(EVENTS.ACTION_SHEET_VIEWED, { type: actionSheetName })));
    },
    [store],
  );

  const { isSMScreenOrBigger } = useScreenSize();

  return (
    <div data-testid="dashboard">
      <ThemeProvider>
        <ReduxProvider store={store}>
          <PersistGate
            loading={<LoadingEmptyState />}
            persistor={persistor}
          >
            <Experimentation.Provider>
              <Auth0Provider
                domain={getAuth0DomainForEnv()}
                clientId={getAuth0ClientIDForEnv()}
                authorizationParams={{
                  redirect_uri: window.location.origin,
                  prompt: 'login', // always prompt user for email and password
                }}
                onRedirectCallback={(appState) => {
                  if (appState?.next) {
                    store.dispatch(setPostLoginRoute(appState?.next));
                  }
                  // Replaces Auth0's default onRedirectCallback, which clears code/state from the URL.
                  // Leaving those params causes Auth0Provider to re-run handleRedirectCallback on
                  // remount/navigation and throw "Invalid state" (transaction already consumed).
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
              >
                <QueryClientProvider client={queryClient}>
                  <SafeAreaProvider>
                    <ToastProvider
                      offset={
                        (isSMScreenOrBigger
                          ? parseInt(SIZES.PAGE.WEB_NAVBAR_HEIGHT.LG.replace('px', ''))
                          : parseInt(SIZES.PAGE.WEB_NAVBAR_HEIGHT.SM.replace('px', ''))) + SPACING.STATIC.MD.value
                      }
                    >
                      <BrowserRouter>
                        <AppVersionWatcher />
                        <ActionSheetProvider createAnalyticsEvent={createAnalyticsEvent}>
                          <NavigationProvider>
                            <Routes>
                              <Route
                                path="/login"
                                element={<Welcome />}
                              />
                              <Route
                                path="/*"
                                element={
                                  <Suspense fallback={<LoadingEmptyState />}>
                                    <WebRoutes />
                                  </Suspense>
                                }
                              />
                            </Routes>
                          </NavigationProvider>
                          <ReactQueryDevtools />
                        </ActionSheetProvider>
                      </BrowserRouter>
                    </ToastProvider>
                  </SafeAreaProvider>
                  <DataLoader />
                </QueryClientProvider>
              </Auth0Provider>
            </Experimentation.Provider>
            <ExperimentationListener />
          </PersistGate>
        </ReduxProvider>
      </ThemeProvider>
    </div>
  );
};

export default Sentry.withProfiler(PatientDashboardApp);
